import { CheckIcon, ClockIcon, WrenchIcon, XIcon } from '@phosphor-icons/react';
import { Tag } from '@/components/kit';
import type { BookingStatus, PublicBooking } from '@/lib/api/schemas';
import { formatInStudio, formatPrice } from '@/lib/format';

export const STATUS_TEXT: Record<BookingStatus, string> = {
  confirmed: 'Подтверждена',
  arrived: 'Машина в работе',
  completed: 'Готово',
  cancelled: 'Отменена',
  no_show: 'Визит не состоялся',
};

const STATUS_ICON = { confirmed: CheckIcon, arrived: WrenchIcon, completed: CheckIcon, cancelled: XIcon, no_show: ClockIcon };

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * The visit at a glance: status and code, the day and a large start time, when the car
 * is ready, then the service, car, box and price. Tinted with the studio colour while
 * the visit is ahead; neutral once it is cancelled or missed.
 */
export function BookingStatusCard({ booking }: { booking: PublicBooking }) {
  const Icon = STATUS_ICON[booking.status];
  const muted = booking.status === 'cancelled' || booking.status === 'no_show';
  const tz = booking.studio.timezone;
  const sameDay = formatInStudio(booking.startsAt, tz, 'yyyy-MM-dd') === formatInStudio(booking.endsAt, tz, 'yyyy-MM-dd');
  const car = [booking.car.model, booking.car.plate].filter(Boolean).join(', ');

  return (
    <div className="ui-ticket" data-muted={muted || undefined}>
      <div className="ui-ticket__top">
        <span className="ui-ticket__status">
          <span className="ui-ticket__mark">
            <Icon size={15} weight="bold" aria-hidden />
          </span>
          {STATUS_TEXT[booking.status]}
        </span>
        <span className="ui-row" style={{ ['--ui-gap' as string]: '8px' }}>
          {booking.isDemo ? <Tag>Демо</Tag> : null}
          <span className="ui-caption ui-num">код {booking.code}</span>
        </span>
      </div>

      <div className="ui-ticket__when">
        <span className="ui-stack" style={{ ['--ui-gap' as string]: '0' }}>
          <span className="ui-ticket__day">{capital(formatInStudio(booking.startsAt, tz, 'EEEE, d MMMM'))}</span>
          <span className="ui-ticket__time ui-num">{formatInStudio(booking.startsAt, tz, 'HH:mm')}</span>
        </span>
        <span className="ui-ticket__end">
          <span className="ui-caption">{sameDay ? 'выдача около' : 'выдача'}</span>
          <span className="ui-ticket__end-value ui-num">
            {sameDay ? formatInStudio(booking.endsAt, tz, 'HH:mm') : formatInStudio(booking.endsAt, tz, 'd MMM, HH:mm')}
          </span>
        </span>
      </div>

      <hr className="ui-ticket__rule" />

      <div className="ui-ticket__service">
        <span className="ui-stack ui-grow" style={{ ['--ui-gap' as string]: '2px' }}>
          <span className="ui-title">{booking.service.name}</span>
          <span className="ui-caption">{[car, booking.resourceName].filter(Boolean).join(' · ')}</span>
        </span>
        <span className="ui-price">
          {formatPrice(booking.service.priceMinor, booking.service.currency, booking.service.priceKind)}
        </span>
      </div>
      <span className="ui-caption">{booking.studio.address}</span>
    </div>
  );
}
